// BUILD-046 correction cycle 1bb88b3e (stage B2b, item N2): the keyboard and state rules of the people combobox of the Assign task dialog.
//
// A people combobox is a text input (role=combobox) that filters the people by display name as you type, with a listbox of the matches that also opens with
// the arrow keys or the dropdown button, and chips for the people already chosen. This module holds what is not drawing: whether the list is open, which
// option is active, what each key does, the element ids that tie the input to its listbox and its active option, and the polite status text. The filtering
// itself is assignModel.searchPeople (a case-insensitive substring of the display name, each person once). People already chosen STAY in the open list,
// marked with a check (aria-selected), so a range of rows stays contiguous; a person is still never chosen twice.
//
// KEYS (the ARIA combobox pattern with a list popup, multi-select by chips):
//   typing            opens the list and makes the first match active
//   ArrowDown / Up    opens the list (first / last option active) or moves the active option, wrapping (highlight only)
//   Shift+Arrow       extends a range: moves the active option one step (no wrapping) and adds every person from the RANGE ANCHOR to it (see decideExtend)
//   Home / End        first / last option, while the list is open
//   Enter             chooses the active option (nothing is submitted by that Enter); with no active option it does nothing here
//   Escape            closes the list and keeps what was typed; with the list already closed it is NOT handled, so the dialog's own Escape applies
//   Backspace         with nothing typed, removes the last chosen person
//   Tab / Shift+Tab   By default (KeyOptions off: the Customer / Job field) they ONLY move focus on as usual (the key is not handled here) and close the
//                     list; they NEVER choose anyone and the typed text is left as it is (Owner direction 2026-10-20, "Tab and typing never choose a job").
//                     The PEOPLE lists of Assign task switch on KeyOptions.tabChoosesHighlighted (Owner direction 2026-10-20 night, narrowly reversing the
//                     2026-10-20 people rule): Tab, with text typed and a name highlighted in the open list, chooses that person, clears the text and closes
//                     the list, and the key is STILL not handled, so focus moves on to the next field as usual. Shift+Tab never chooses.
// Enter clears the typed text and closes the list; focus stays in the input.
//
// WHO IS CHOSEN: an explicit gesture adds a person - a mouse click on a row (with Shift: a range), Enter on the highlighted result, Shift+Arrow (a range),
// or, in the people lists only, Tab with text typed and a name highlighted. Typing never adds anyone (it filters and highlights the first match), opening the
// list never adds anyone, and Shift+Tab, Escape, a click outside or leaving the field by the mouse add nobody. Tab with nothing typed, or with nothing
// matching (so nothing highlighted), adds nobody either. A person already chosen is never added twice.
//
// MOUSE: a click adds that person and makes them the RANGE ANCHOR; the list STAYS OPEN and the typed filter stays (afterMousePick), so a Shift-click on
// another row adds EVERY person between the anchor and the clicked row, inclusive, in the order shown (decidePick). Escape, Tab, a click outside (closes
// only, chooses nobody), or the dropdown button close the list.
//
// Pure module: no React, no DOM, no I/O. Erasable TypeScript only.

export interface ComboState {
  open: boolean;
  query: string;
  /** Index into the current options, or -1 for none. */
  active: number;
}

export function initialCombo(): ComboState {
  return { open: false, query: '', active: -1 };
}

/** What the person typed: the list opens and the first match becomes active. */
export function typeQuery(state: ComboState, query: string): ComboState {
  return { open: true, query: typeof query === 'string' ? query : '', active: 0 };
}

/** The dropdown button: opens the list at the first option, or closes it. The typed text stays. */
export function toggleList(state: ComboState): ComboState {
  return state.open ? { ...state, open: false, active: -1 } : { ...state, open: true, active: 0 };
}

/** The input got focus by a click or the list was asked for: open it (used by the button and by ArrowDown). */
export function openList(state: ComboState): ComboState {
  return state.open ? state : { ...state, open: true, active: 0 };
}

export function closeList(state: ComboState): ComboState {
  return state.open || state.active !== -1 ? { ...state, open: false, active: -1 } : state;
}

export type Move = 'next' | 'previous' | 'first' | 'last';

/** Moves the active option among `count` options, wrapping. A closed list is opened first (ArrowDown at the first option, ArrowUp at the last). */
export function moveActive(state: ComboState, move: Move, count: number): ComboState {
  if (count <= 0) return state.open ? { ...state, active: -1 } : { ...state, open: true, active: -1 };
  if (!state.open) return { ...state, open: true, active: move === 'previous' || move === 'last' ? count - 1 : 0 };
  let next: number;
  if (move === 'first') next = 0;
  else if (move === 'last') next = count - 1;
  else if (state.active < 0 || state.active >= count) next = move === 'next' ? 0 : count - 1;
  else next = move === 'next' ? (state.active + 1) % count : (state.active - 1 + count) % count;
  return { ...state, active: next };
}

/** The active index kept inside the options (the list shrinks while typing). */
export function clampActive(state: ComboState, count: number): ComboState {
  if (!state.open) return state;
  const active = count <= 0 ? -1 : state.active < 0 ? 0 : Math.min(state.active, count - 1);
  return active === state.active ? state : { ...state, active };
}

/** After a person is chosen: the typed text goes, the list closes. */
export function afterChoose(): ComboState {
  return initialCombo();
}

export interface KeyDecision {
  /** The key was handled here: the caller prevents the browser default. */
  handled: boolean;
  /** Stop the key from reaching the dialog (Escape that closed the list). */
  stop: boolean;
  state: ComboState;
  /** Set when Enter chose an option (the id of the person). */
  choose: string | null;
  /** Set when Backspace on an empty field asks to remove the last chosen person. */
  removeLast: boolean;
}

export interface OptionLike {
  employeeId: string;
}

export interface KeyOptions {
  /**
   * PEOPLE LISTS ONLY (Owner direction 2026-10-20 night). Default OFF. When on, Tab (not Shift+Tab) with text typed and a name highlighted in the open
   * list chooses that person (and clears the text and closes the list); the key is still not handled, so focus moves on. The Customer / Job field never
   * sets this: its Owner rule is that Tab and typing never choose a job.
   */
  tabChoosesHighlighted?: boolean;
  /** Shift was held with the key. Only read for Tab: Shift+Tab never chooses. */
  shiftKey?: boolean;
  /** The ids already chosen; a highlighted person who is already chosen is not chosen again. */
  chosenIds?: readonly string[];
}

/** What a key does. `options` are the matches shown now (people already chosen included); `selectedCount` is how many chips there are. */
export function decideKey(key: string, state: ComboState, options: readonly OptionLike[], selectedCount: number, keyOptions: KeyOptions = {}): KeyDecision {
  const none: KeyDecision = { handled: false, stop: false, state, choose: null, removeLast: false };
  const count = options.length;
  switch (key) {
    case 'ArrowDown':
      return { ...none, handled: true, state: moveActive(state, 'next', count) };
    case 'ArrowUp':
      return { ...none, handled: true, state: moveActive(state, 'previous', count) };
    case 'Home':
      return state.open && count > 0 ? { ...none, handled: true, state: moveActive(state, 'first', count) } : none;
    case 'End':
      return state.open && count > 0 ? { ...none, handled: true, state: moveActive(state, 'last', count) } : none;
    case 'Enter': {
      // Enter inside an open list with an active option chooses it; otherwise it is left alone (it must not submit the form from an open list either).
      if (state.open && state.active >= 0 && state.active < count) return { ...none, handled: true, choose: options[state.active].employeeId, state: afterChoose() };
      return state.open ? { ...none, handled: true } : none;
    }
    case 'Escape':
      return state.open ? { ...none, handled: true, stop: true, state: closeList(state) } : none;
    case 'Backspace':
      return state.query === '' && selectedCount > 0 ? { ...none, handled: true, removeLast: true } : none;
    case 'Tab': {
      // The key is 'Tab' for both Tab and Shift+Tab. Either way the key is NOT handled, so the browser moves focus on as usual.
      // Default (and Shift+Tab, and the Customer / Job field): the list closes, the typed text stays, nobody is chosen (Owner direction 2026-10-20).
      const leave: KeyDecision = state.open ? { ...none, state: closeList(state) } : none;
      if (keyOptions.tabChoosesHighlighted !== true || keyOptions.shiftKey === true) return leave;
      // People lists (Owner direction 2026-10-20 night): text typed AND the list open with a name highlighted -> that person is chosen. Nothing typed (an
      // opened list), nothing highlighted (no match, or the list was closed) and a person already chosen all just leave, as above.
      const typed = typeof state.query === 'string' && state.query.trim() !== '';
      const highlighted = typed && state.open && state.active >= 0 && state.active < count ? options[state.active].employeeId : null;
      if (highlighted === null || (keyOptions.chosenIds ?? []).includes(highlighted)) return leave;
      return { ...none, choose: highlighted, state: afterChoose() };
    }
    default:
      return none;
  }
}

/** The polite status line when Tab added a person (the list has closed, so the usual "N people found" line is gone). */
export function describeTabAdd(displayName: string): string {
  return `${typeof displayName === 'string' && displayName.trim() !== '' ? displayName.trim() : 'Person'} added`;
}

/** A stable, id-safe element id for one option. */
export function optionId(baseId: string, employeeId: string): string {
  return `${baseId}-opt-${String(employeeId).replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

export interface ComboView {
  expanded: boolean;
  /** The id of the active option, for aria-activedescendant; undefined when none (the attribute is then left out). */
  activeDescendant: string | undefined;
  /** Text for a polite live region: how many matches, or that none match. Empty while the list is closed. */
  status: string;
  /** The listbox shows an empty line instead of options. */
  empty: boolean;
}

export function describeCombo(state: ComboState, options: readonly OptionLike[], baseId: string, loading: boolean): ComboView {
  const count = options.length;
  const activeOption = state.open && state.active >= 0 && state.active < count ? options[state.active] : null;
  let status = '';
  if (state.open) {
    if (loading) status = 'Loading people…';
    else if (count === 0) status = state.query.trim() === '' ? 'No more people to choose.' : 'No people match.';
    else status = `${count} ${count === 1 ? 'person' : 'people'} found. Use the arrow keys, then Enter to choose.`;
  }
  return { expanded: state.open, activeDescendant: activeOption === null ? undefined : optionId(baseId, activeOption.employeeId), status, empty: state.open && count === 0 };
}

// ───────────────────────── ranges: Shift-click and Shift+Arrow ─────────────────────────

/**
 * Every id from the anchor to the target, both included, in the order shown (top to bottom, whichever way the range was drawn). `visibleIds` is the list as
 * it is shown now (the filtered list when the person typed a filter). With no anchor, or an anchor that is not shown (a typed filter hides it), the range is
 * just the target; a target that is not shown gives nothing.
 */
export function rangeBetween(visibleIds: readonly string[], anchorId: string | null, targetId: string): string[] {
  const target = visibleIds.indexOf(targetId);
  if (target < 0) return [];
  const anchor = anchorId === null ? -1 : visibleIds.indexOf(anchorId);
  if (anchor < 0) return [targetId];
  return visibleIds.slice(Math.min(anchor, target), Math.max(anchor, target) + 1);
}

/** The chosen ids with `additions` appended: nobody twice, the people already chosen keep their place, new people follow in the order given. */
export function mergeChosen(chosenIds: readonly string[], additions: readonly string[]): string[] {
  const merged: string[] = [];
  const seen = new Set<string>();
  for (const id of [...chosenIds, ...additions]) {
    if (typeof id !== 'string' || id === '' || seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }
  return merged;
}

export interface PickDecision {
  /** The people to add now, in the order shown: nobody already chosen, nobody twice. Empty when the click changes no one. */
  toAdd: string[];
  /** The range anchor after the click: the clicked person (unchanged when the clicked person is not shown). */
  anchor: string | null;
}

/**
 * What a mouse click on `targetId` does. A plain click adds that person; Shift+click adds every person from the anchor to the clicked one (inclusive, in the
 * order shown), or just the clicked one when there is no usable anchor. People already chosen are skipped, so clicking a chosen row (plain or in a range)
 * adds no one and removes no one; removal stays with the chip's own button. The clicked person becomes the anchor either way.
 */
export function decidePick(visibleIds: readonly string[], chosenIds: readonly string[], anchorId: string | null, targetId: string, shift: boolean): PickDecision {
  if (!visibleIds.includes(targetId)) return { toAdd: [], anchor: anchorId };
  const wanted = shift ? rangeBetween(visibleIds, anchorId, targetId) : [targetId];
  const before = new Set(chosenIds);
  return { toAdd: mergeChosen(chosenIds, wanted).filter((id) => !before.has(id)), anchor: targetId };
}

/** After a mouse pick the list STAYS OPEN with the typed filter kept; only the active (highlighted) option moves to the clicked row. */
export function afterMousePick(state: ComboState, index: number): ComboState {
  return { ...state, open: true, active: index };
}

export interface ExtendDecision {
  /** The key was handled here: the caller prevents the browser default. */
  handled: boolean;
  state: ComboState;
  /** People to add now (in the order shown, nobody already chosen). */
  toAdd: string[];
  /** The range anchor after the key. */
  anchor: string | null;
}

/**
 * Shift+ArrowDown / Shift+ArrowUp: moves the active option one step (clamped at both ends: a range never wraps around the list) and adds every person from
 * the range anchor to the newly active option. With no usable anchor the anchor becomes the option that was active before the move (or the new one when
 * nothing was active), so Shift+Down from a highlighted row adds that row and the next. The anchor does not move while extending. On a closed list it only
 * opens the list, like the plain arrow. Any other key is not handled here.
 */
export function decideExtend(key: string, state: ComboState, options: readonly OptionLike[], anchorId: string | null, chosenIds: readonly string[]): ExtendDecision {
  const none: ExtendDecision = { handled: false, state, toAdd: [], anchor: anchorId };
  if (key !== 'ArrowDown' && key !== 'ArrowUp') return none;
  const count = options.length;
  const down = key === 'ArrowDown';
  if (!state.open || count === 0) return { ...none, handled: true, state: moveActive(state, down ? 'next' : 'previous', count) };
  const from = state.active >= 0 && state.active < count ? state.active : -1;
  const target = down ? (from < 0 ? 0 : Math.min(from + 1, count - 1)) : from < 0 ? count - 1 : Math.max(from - 1, 0);
  const visibleIds = options.map((o) => o.employeeId);
  const targetId = options[target].employeeId;
  const anchor = anchorId !== null && visibleIds.includes(anchorId) ? anchorId : from >= 0 ? options[from].employeeId : targetId;
  const before = new Set(chosenIds);
  const toAdd = mergeChosen(chosenIds, rangeBetween(visibleIds, anchor, targetId)).filter((id) => !before.has(id));
  return { handled: true, state: target === state.active ? state : { ...state, active: target }, toAdd, anchor };
}

// ───────────────────────── placeholder ─────────────────────────

/** What the empty field says once two or more people are chosen (the host's full prompt would be cut off beside two long chips). */
export const ADD_ANOTHER_PLACEHOLDER = 'Add another person';

/** The field's placeholder: the host's own text while there are no chips or one chip (as in the approved layout), a short prompt from two chips on. */
export function placeholderFor(chipCount: number, fullText: string): string {
  return chipCount >= 2 ? ADD_ANOTHER_PLACEHOLDER : fullText;
}
